// ANNUAL Australian resident tax liability for an FY: income tax + Medicare
// levy + Budget Repair Levy + Medicare levy surcharge + Division 293 tax +
// compulsory study/training loan repayment — compared with tax withheld to
// estimate the refund/amount owing. Pure; all amounts are integer CENTS.
//
// Taxable income is assessed in whole dollars (cents ignored), as on a
// notice of assessment. See tax-tables.js for the sources + known gaps.

import { daysBetween, fyEnd, fyStart } from '../fy.js';
import { roundMinor } from '../money.js';
import {
  LATEST_THRESHOLD_FY,
  MLS_CHILD_STEP,
  MLS_FALLBACK_NOTE,
  MLS_SPOUSE_LOW_INCOME,
  RESIDENT_BRACKETS,
  budgetRepairLevy,
  helpRepaymentTable,
  medicareLevyRate,
  mlsTable,
  residentBrackets,
} from './tax-tables.js';
import { EXCESS_CC_OFFSET } from './super-tables.js';

const toDollars = (cents) => Math.floor(Math.max(0, Math.round(cents ?? 0)) / 100);
const dollarsToCents = (dollars) => roundMinor(dollars * 100);

/** The FYs with their own resident brackets (ascending); later FYs reuse the last. */
export const SUPPORTED_FYS = Object.keys(RESIDENT_BRACKETS).map(Number);

/** Resident income tax (no levies/offsets) on taxable income, in cents. */
export function incomeTax(fy, taxableIncomeCents) {
  const brackets = residentBrackets(fy);
  if (!brackets) throw new RangeError(`No resident tax rates for FY ${fy}`);
  const income = toDollars(taxableIncomeCents);
  let tax = 0;
  for (const [threshold, base, rate] of brackets) {
    if (income > threshold) tax = base + rate * (income - threshold);
  }
  return dollarsToCents(tax);
}

/** The marginal income-tax rate at a taxable income (excl. Medicare). */
export function marginalRate(fy, taxableIncomeCents) {
  const income = toDollars(taxableIncomeCents);
  let rate = 0;
  for (const [threshold, , r] of residentBrackets(fy) ?? []) if (income > threshold) rate = r;
  return rate;
}

/** Days in an FY (365 or 366). */
export function daysInFy(fy) {
  return daysBetween(fyStart(fy), fyEnd(fy)) + 1;
}

function mlsTier(bounds, income) {
  const i = bounds.findIndex((upper) => income <= upper);
  return i === -1 ? bounds.length : i;
}

/**
 * Medicare levy surcharge (cents). Charged at 1% / 1.25% / 1.5% of taxable
 * income + reportable fringe benefits, pro-rata for the days WITHOUT
 * appropriate private hospital cover. The tier uses income for MLS purposes —
 * your own (single) or combined with your spouse's (family; the family
 * thresholds rise $1,500 per dependent child after the first). With a
 * spouse, no MLS if your own MLS income is at or below the low-income figure.
 */
export function medicareLevySurcharge({
  fy,
  mlsIncomeCents,
  levyBaseCents,
  daysWithoutCover,
  spouse = false,
  spouseMlsIncomeCents = 0,
  dependentChildren = 0,
}) {
  const table = mlsTable(fy);
  const days = Math.min(Math.max(0, daysWithoutCover ?? 0), daysInFy(fy));
  const own = toDollars(mlsIncomeCents);
  const empty = { cents: 0, rate: 0, tier: 0, fallback: table.fallback };
  if (days === 0) return empty;
  if (spouse && own <= MLS_SPOUSE_LOW_INCOME) return empty;
  const family = spouse || dependentChildren > 0;
  let tier;
  if (family) {
    const step = MLS_CHILD_STEP * Math.max(0, dependentChildren - 1);
    const combined = own + (spouse ? toDollars(spouseMlsIncomeCents) : 0);
    tier = mlsTier(
      table.family.map((b) => b + step),
      combined,
    );
  } else {
    tier = mlsTier(table.single, own);
  }
  const rate = table.rates[tier];
  const cents = roundMinor((toDollars(levyBaseCents) * 100 * rate * days) / daysInFy(fy));
  return { cents, rate, tier, fallback: table.fallback };
}

/** Division 293 threshold: $300k to 2016–17, $250k from 2017–18. */
export function div293Threshold(fy) {
  return fy >= 2018 ? 250_000 : 300_000;
}

/**
 * Division 293 tax (cents): 15% of the lesser of your low-tax (concessional)
 * contributions and the amount by which Div 293 income + those contributions
 * exceeds the threshold. Applies from 2012–13.
 */
export function div293Tax(fy, div293IncomeCents, lowTaxContributionsCents) {
  if (fy < 2013) return 0;
  const income = toDollars(div293IncomeCents);
  const contributions = toDollars(lowTaxContributionsCents);
  const excess = Math.max(0, income + contributions - div293Threshold(fy));
  return dollarsToCents(0.15 * Math.min(contributions, excess));
}

/**
 * Compulsory HELP/STSL repayment (cents) on repayment income, or `null` when
 * no table exists for the FY (before 2020–21).
 */
export function studyLoanRepayment(fy, repaymentIncomeCents) {
  const table = helpRepaymentTable(fy);
  if (!table) return null;
  const income = toDollars(repaymentIncomeCents);
  if (table.marginal) {
    const { t0, t1, base1, t2 } = table.marginal;
    if (income <= t0) return 0;
    if (income <= t1) return dollarsToCents(0.15 * (income - t0));
    if (income <= t2) return dollarsToCents(base1 + 0.17 * (income - t1));
    return dollarsToCents(0.1 * income);
  }
  let rate = 0;
  table.bands.forEach((lower, i) => {
    if (income >= lower) rate = table.rates[i];
  });
  return dollarsToCents(rate * income);
}

/**
 * Full annual estimate. All money inputs are cents.
 * @param {object} p
 * @param {number} p.fy
 * @param {number} p.taxableIncomeCents
 * @param {number} [p.reportableFringeBenefitsCents]
 * @param {number} [p.netInvestmentLossCents]
 * @param {number} [p.reportableSuperCents] — RESC (salary sacrifice) + deductible personal contributions.
 * @param {number} [p.exemptForeignIncomeCents]
 * @param {number} [p.lowTaxContributionsCents] — concessional contributions (excl. excess) for Div 293.
 * @param {number} [p.daysWithoutCover] — days without appropriate private hospital cover.
 * @param {object} [p.family] — { spouse, spouseMlsIncomeCents, dependentChildren } (whole-year).
 * @param {boolean} [p.studyLoan] — has a HELP/STSL debt.
 * @param {number} [p.withheldCents] — total PAYG withheld (incl. STSL components).
 * @param {number} [p.excessConcessionalCents] — excess concessional contributions (see super.js).
 * @param {number} [p.nonRefundableOffsetsCents] — e.g. the foreign income tax offset: reduces
 *   income tax, Medicare levy, the surcharge and the budget repair levy, never below nil.
 * @param {number} [p.refundableOffsetsCents] — e.g. franking credits: any excess is refunded.
 * @returns {object} breakdown in cents + `balanceCents` (positive = refund) + `notes`.
 */
export function annualTax({
  fy,
  taxableIncomeCents,
  reportableFringeBenefitsCents = 0,
  netInvestmentLossCents = 0,
  reportableSuperCents = 0,
  exemptForeignIncomeCents = 0,
  lowTaxContributionsCents = 0,
  daysWithoutCover = 0,
  family = {},
  studyLoan = false,
  withheldCents = 0,
  excessConcessionalCents = 0,
  nonRefundableOffsetsCents = 0,
  refundableOffsetsCents = 0,
}) {
  const notes = [];
  if (fy > LATEST_THRESHOLD_FY) {
    const latest = `${LATEST_THRESHOLD_FY - 1}–${String(LATEST_THRESHOLD_FY % 100).padStart(2, '0')}`;
    notes.push(
      `Uses currently legislated tax rates; thresholds not yet published for this year (Medicare levy surcharge, study loan) use ${latest}’s.`,
    );
  }
  // Excess concessional contributions are assessable (taxable) income, with a
  // 15% non-refundable offset for the tax already paid in the fund (2013–14+).
  const taxable = toDollars(taxableIncomeCents + Math.max(0, excessConcessionalCents)) * 100;
  const grossTax = incomeTax(fy, taxable);
  const excessOffset =
    fy >= 2014
      ? Math.min(grossTax, roundMinor(Math.max(0, excessConcessionalCents) * EXCESS_CC_OFFSET))
      : 0;
  const tax = grossTax - excessOffset;
  const medicareLevy = roundMinor(taxable * medicareLevyRate(fy));
  const brl = budgetRepairLevy(fy);
  const budgetRepair = brl ? roundMinor(Math.max(0, taxable - brl.threshold * 100) * brl.rate) : 0;

  // Income for MLS purposes / Div 293 income / repayment income share these parts.
  const extras = reportableFringeBenefitsCents + netInvestmentLossCents + exemptForeignIncomeCents;
  const mls = medicareLevySurcharge({
    fy,
    mlsIncomeCents: taxable + extras + reportableSuperCents,
    levyBaseCents: taxable + reportableFringeBenefitsCents,
    daysWithoutCover,
    spouse: !!family.spouse,
    spouseMlsIncomeCents: family.spouseMlsIncomeCents ?? 0,
    dependentChildren: family.dependentChildren ?? 0,
  });
  if (mls.fallback && daysWithoutCover > 0) notes.push(MLS_FALLBACK_NOTE);

  const div293 = div293Tax(fy, taxable + extras, lowTaxContributionsCents);

  let studyLoanCents = 0;
  if (studyLoan) {
    const repayment = studyLoanRepayment(fy, taxable + extras + reportableSuperCents);
    if (repayment === null) notes.push('No study-loan repayment table for this year.');
    else studyLoanCents = repayment;
  }

  const dollars = taxable / 100;
  if (dollars < 66_667) {
    notes.push('Low income tax offset and the Medicare low-income reduction are not modelled.');
  } else if (fy >= 2019 && fy <= 2022 && dollars < 126_000) {
    notes.push('Low and middle income tax offset (2018–19 to 2021–22) is not modelled.');
  }

  // Non-refundable offsets only reduce the tax they can absorb (not Div 293 or
  // study-loan repayments); refundable ones come off everything, excess paid out.
  const absorbable = tax + medicareLevy + budgetRepair + mls.cents;
  const nonRefundable = Math.min(absorbable, Math.max(0, nonRefundableOffsetsCents));
  const refundable = Math.max(0, refundableOffsetsCents);
  const totalCents = absorbable - nonRefundable + div293 + studyLoanCents - refundable;
  return {
    fy,
    taxableIncomeCents: taxable,
    incomeTaxCents: tax,
    excessConcessionalOffsetCents: excessOffset,
    medicareLevyCents: medicareLevy,
    budgetRepairLevyCents: budgetRepair,
    mlsCents: mls.cents,
    mlsRate: mls.rate,
    div293Cents: div293,
    studyLoanCents,
    nonRefundableOffsetsCents: nonRefundable,
    refundableOffsetsCents: refundable,
    totalCents,
    withheldCents,
    balanceCents: withheldCents - totalCents,
    notes,
  };
}
